import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import { isPlainObject } from 'lodash';
import apolloClient from 'services/apollo-client';
import { SurfaceNodePointsTo, SurfaceNodeUnion } from 'graphql/types';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import { notEmpty } from 'utils';

interface UseFindNode {
  findNodeById: (input: FetchNodeByIdInput | string) => SurfaceNodeUnion;
  findNodeFromLink: (input: SurfaceNodeUnion) => SurfaceNodeUnion;
  findReverseLink(link: SurfaceNodePointsTo): SurfaceNodePointsTo | undefined | null;
}

type FetchNodeByIdInput = {
  id?: string;
  cache?: any;
  surface?: string;
};

export default function useFindNodeInCache(surfaceId?: string): UseFindNode {
  const currentSurface = useCurrentSurface();

  surfaceId = surfaceId ?? currentSurface?.id;

  const fetchNodesOnSurfaceFromCache = useFetchSurfaceNodesFromCache(surfaceId);

  function findNodeById(input: FetchNodeByIdInput | string): SurfaceNodeUnion {
    if (isPlainObject(input)) {
      const {
        id,
        cache = apolloClient,
        surface = surfaceId,
      } = input as FetchNodeByIdInput;

      return findNode(fetchNodesOnSurfaceFromCache({ surface, cache }), id);
    }

    return findNode(fetchNodesOnSurfaceFromCache(), input);
  }

  function findNodeFromLink({
    surface,
    id,
  }: SurfaceNodeUnion): SurfaceNodeUnion {
    const nodesFromCache = fetchNodesOnSurfaceFromCache({
      surface: surface?.id ?? surfaceId,
    });

    return findNode(nodesFromCache, id);
  }

  function findNode(nodes, id) {
    const node = nodes.find((node) => node.id === id);
    if (node) {
      return node;
    }

    return nodes
      .map((node) => node.snippets)
      .filter(Boolean)
      .flat()
      .find((snippetNode) => snippetNode.id === id);
  }


  function findReverseLink(
    link: SurfaceNodePointsTo,
  ): SurfaceNodePointsTo | undefined | null {
    return fetchNodesOnSurfaceFromCache()
      .map(({ pointsTo }) => pointsTo)
      .filter(notEmpty)
      .flat()
      .find(
        (pointsTo) =>
          pointsTo.to.id === link.from.id && pointsTo.from.id === link.to.id,
      );
  }

  return { findNodeById, findNodeFromLink, findReverseLink };
}
